import { ReactNode, useEffect } from "react";
import { createPortal } from "react-dom";

import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { XMarkIcon } from "@heroicons/react/24/outline";

import { IProductionDashboardBay } from "../../models/ProductionDashboard";

interface TvOverlayShellProps {
  now: DateTime;
  /** Rendered between the clock and the close button - KPI pills for Bays, headline tiles for Line. */
  headerContent?: ReactNode;
  footer?: ReactNode;
  /**
   * Must be referentially stable (useCallback). The fullscreen effect re-runs when it changes:
   * the cleanup's exitFullscreen() then completes after the re-run has requested fullscreen again,
   * and the resulting fullscreenchange would close the overlay on the next tick.
   */
  onClose: () => void;
  /** Direct flex children of the overlay; the body root carries its own flex-1 min-h-0. */
  children: ReactNode;
}

const vehicleName = (v: { combinedMake: string | null; combinedModel: string | null }) =>
  [v.combinedMake, v.combinedModel].filter(Boolean).join(" ");

// The dark, large-type shell both TV modes share. Covers the whole window (and asks the browser
// for fullscreen, which it may refuse - the overlay works either way); Esc, leaving fullscreen or
// the corner button all close it.
export default function TvOverlayShell({
  now,
  headerContent,
  footer,
  onClose,
  children,
}: TvOverlayShellProps) {
  const { t } = useTranslation("production");

  useEffect(() => {
    const root = document.documentElement;
    let requested = false;

    if (root.requestFullscreen && !document.fullscreenElement) {
      requested = true;
      root.requestFullscreen().catch(() => {
        requested = false;
      });
    }

    const handleFullscreenChange = () => {
      // Only a *departure* from fullscreen closes the overlay - the entry fires this event too.
      if (requested && !document.fullscreenElement) onClose();
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("keydown", handleKeyDown);
      if (document.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen().catch(() => undefined);
      }
    };
  }, [onClose]);

  // Portalled to <body>: DashboardLayout's <main> is its own stacking context (z-index 0), so an
  // overlay rendered inside it can never rise above the sidebar, topbar or chat bubble.
  return createPortal(
    <div
      className="fixed inset-0 z-[1100] flex flex-col gap-5 bg-gray-950 p-8 text-gray-50 overflow-hidden"
      role="dialog"
      aria-modal="true"
      aria-label={t("TV mode")}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-baseline gap-5 shrink-0">
          <span className="text-6xl font-semibold leading-none tabular-nums">
            {now.toFormat("HH:mm")}
          </span>
          <span className="text-xl text-gray-400">{now.toFormat("cccc d LLLL")}</span>
        </div>
        <div className="flex items-center gap-3 min-w-0">
          {headerContent}
          <button
            type="button"
            onClick={onClose}
            className="ml-2 inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white"
            aria-label={t("Exit TV mode")}
            title={t("Exit TV mode")}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
      </div>

      {children}

      {footer}
    </div>,
    document.body
  );
}

interface TvDueNextStripProps {
  bays: IProductionDashboardBay[];
  /** Right-aligned text after the list, e.g. the Triage / Depollution counts. */
  trailing?: ReactNode;
}

// The footer strip: the first vehicle due on each line or bay, from the Production Planner.
export function TvDueNextStrip({ bays, trailing }: TvDueNextStripProps) {
  const { t } = useTranslation("production");

  const dueNext = bays
    .map((bay) => ({ bay, vehicle: bay.dueNext?.[0] }))
    .filter((entry) => !!entry.vehicle);

  return (
    <div className="flex items-center gap-5 rounded-xl border border-gray-800 bg-gray-900 px-6 h-16 text-xl overflow-hidden shrink-0">
      <span className="text-sm font-bold uppercase tracking-widest text-gray-400 shrink-0">
        {t("Due on next")}
      </span>
      <div className="flex items-center gap-5 min-w-0 overflow-hidden whitespace-nowrap">
        {dueNext.length === 0 ? (
          <span className="text-gray-400">{t("Nothing planned")}</span>
        ) : (
          dueNext.map(({ bay, vehicle }, i) => (
            <span key={bay.id} className="flex items-center gap-5">
              {i > 0 && <span className="text-gray-700">|</span>}
              <span>
                <strong>{bay.name}</strong>{" "}
                <span className="text-gray-300">
                  {vehicle && [vehicle.vrm, vehicleName(vehicle)].filter(Boolean).join(" ")}
                </span>
              </span>
            </span>
          ))
        )}
      </div>
      {trailing && <span className="ml-auto shrink-0 text-base text-gray-400">{trailing}</span>}
    </div>
  );
}
